// Firebase config & init
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, runTransaction, collection, query, where, getDocs, getDoc, doc, updateDoc } from 'firebase/firestore'; // Use initializeFirestore for better mobile support
import { getStorage } from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Use initializeFirestore with mobile-optimized settings
// experimentalAutoDetectLongPolling: Automatically uses long polling on mobile/unstable networks
// This prevents WebChannel 404 errors by using a more reliable transport
export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
  ignoreUndefinedProperties: true
});
export const storage = getStorage(app);
// Only initialize analytics if in browser environment
let analytics;
if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
  analytics = getAnalytics(app);
}

// --- Stock Update Functions ---

/**
 * Updates the stock of a material upon approval of a request.
 * Handles unit conversion between gram and mg.
 * @param {string} namaBahan - The name of the material.
 * @param {number} jumlahDiminta - The amount requested.
 * @param {string} satuanDiminta - The unit of the requested amount (e.g., 'ml', 'gram', 'mg').
 */
export const updateBahanStockOnApproval = async (namaBahan, jumlahDiminta, satuanDiminta) => {
  if (!namaBahan || !jumlahDiminta || !satuanDiminta) {
    console.error("updateBahanStockOnApproval: Missing required parameters.");
    throw new Error("Parameter tidak lengkap untuk update stok bahan.");
  }

  const bahanRef = collection(db, 'bahan');
  const q = query(bahanRef, where('nama', '==', namaBahan));

  try {
    await runTransaction(db, async (transaction) => {
      const querySnapshot = await getDocs(q); // Get docs within transaction? Firestore docs recommend getting snapshot outside if possible, but let's try inside for consistency. Re-fetch might be needed if done outside.
      // Re-querying inside transaction for atomicity might be safer if reads depend on writes. Let's query outside and get inside.
      // const querySnapshot = await getDocs(q); // Query outside transaction

      if (querySnapshot.empty) {
        throw new Error(`Bahan "${namaBahan}" tidak ditemukan di database.`);
      }
      if (querySnapshot.size > 1) {
        console.warn(`Peringatan: Ditemukan ${querySnapshot.size} bahan dengan nama "${namaBahan}". Menggunakan yang pertama.`);
      }

      const bahanDocRef = querySnapshot.docs[0].ref; // Get the actual DocumentReference
      const bahanDoc = await transaction.get(bahanDocRef); // Read within transaction

      if (!bahanDoc.exists()) {
        throw new Error(`Bahan "${namaBahan}" tidak ditemukan dalam transaksi.`);
      }

      const currentData = bahanDoc.data();
      const currentStock = Number(currentData.stok) || 0;
      const stockSatuan = currentData.satuan; // Unit in stock (e.g., 'gram', 'ml', 'mg')

      let deductionAmount = Number(jumlahDiminta);

      // Normalize units to lowercase for case-insensitive comparison
      const normalizedStockSatuan = stockSatuan.toLowerCase();
      const normalizedSatuanDiminta = satuanDiminta.toLowerCase();

      // Unit Conversion Logic
      if (normalizedStockSatuan === 'gram' && normalizedSatuanDiminta === 'mg') {
        deductionAmount = deductionAmount / 1000;
      } else if (normalizedStockSatuan === 'mg' && normalizedSatuanDiminta === 'gram') {
        deductionAmount = deductionAmount * 1000;
      } else if (normalizedStockSatuan !== normalizedSatuanDiminta && (normalizedStockSatuan === 'ml' || normalizedSatuanDiminta === 'ml')) {
        // Prevent conversion between volume and mass unless explicitly defined
        throw new Error(`Konversi satuan tidak didukung antara "${stockSatuan}" dan "${satuanDiminta}" untuk bahan "${namaBahan}".`);
      } else if (normalizedStockSatuan !== normalizedSatuanDiminta) {
         // Allow g <-> mg, but warn/error on other mismatches like pcs vs gram? For now, assume g/mg are main powder units.
         console.warn(`Satuan stok (${stockSatuan}) berbeda dengan satuan diminta (${satuanDiminta}) untuk ${namaBahan}, namun bukan gram/mg. Menggunakan jumlah diminta secara langsung.`);
         // Or throw error: throw new Error(`Satuan tidak cocok: Stok (${stockSatuan}), Diminta (${satuanDiminta})`);
      }
       // If units match (ml/ml, gram/gram, mg/mg), deductionAmount remains jumlahDiminta

      const newStock = currentStock - deductionAmount;

      if (newStock < 0) {
        console.warn(`Peringatan: Stok bahan "${namaBahan}" menjadi negatif (${newStock}) setelah dikurangi ${deductionAmount} ${stockSatuan}.`);
        // Decide whether to throw error or allow negative stock
        // throw new Error(`Stok bahan "${namaBahan}" tidak mencukupi.`);
      }

      transaction.update(bahanDocRef, { stok: newStock });
      console.log(`Stok bahan "${namaBahan}" berhasil diperbarui menjadi ${newStock} ${stockSatuan}.`);
    });
  } catch (error) {
    console.error(`Error updating stock for material "${namaBahan}":`, error);
    // Re-throw the error so the calling component knows it failed
    throw new Error(`Gagal memperbarui stok untuk ${namaBahan}: ${error.message}`);
  }
};

/**
 * Updates the stock of a glassware item upon loan approval.
 * @param {string} namaAlat - The name of the glassware.
 * @param {number} jumlahDipinjam - The quantity being borrowed.
 */
export const updateAlatGelasStockOnLoanApproval = async (namaAlat, jumlahDipinjam) => {
   if (!namaAlat || jumlahDipinjam === undefined || jumlahDipinjam === null) {
    console.error("updateAlatGelasStockOnLoanApproval: Missing required parameters.");
    throw new Error("Parameter tidak lengkap untuk update stok alat gelas.");
  }
  const amountToDecrease = Number(jumlahDipinjam);
   if (isNaN(amountToDecrease) || amountToDecrease <= 0) {
     throw new Error("Jumlah dipinjam harus angka positif.");
   }

  const alatRef = collection(db, 'alat_gelas');
  const q = query(alatRef, where('nama', '==', namaAlat));

  try {
    // Query to find the document
    const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error(`Alat gelas "${namaAlat}" tidak ditemukan di database.`);
      }
      if (querySnapshot.size > 1) {
        console.warn(`Peringatan: Ditemukan ${querySnapshot.size} alat gelas dengan nama "${namaAlat}". Menggunakan yang pertama.`);
      }

      const alatDocRef = querySnapshot.docs[0].ref;
    
    // Read current stock (outside transaction to avoid quota issues)
    const alatDoc = await getDoc(alatDocRef);

      if (!alatDoc.exists()) {
      throw new Error(`Alat gelas "${namaAlat}" tidak ditemukan.`);
      }

      const currentData = alatDoc.data();
      const currentStock = Number(currentData.stok) || 0; // Ensure stok field exists and is a number
      const newStock = currentStock - amountToDecrease;

      if (newStock < 0) {
        console.warn(`Peringatan: Stok alat gelas "${namaAlat}" menjadi negatif (${newStock}) setelah dikurangi ${amountToDecrease}.`);
        // throw new Error(`Stok alat gelas "${namaAlat}" tidak mencukupi.`);
      }

    // Update directly without transaction (simpler and avoids quota exceeded)
    await updateDoc(alatDocRef, { stok: newStock });

    console.log(`Stok alat gelas "${namaAlat}" berhasil diperbarui dari ${currentStock} menjadi ${newStock}.`);
  } catch (error) {
    console.error(`Error updating stock for glassware "${namaAlat}" on loan:`, error);
    throw new Error(`Gagal memperbarui stok untuk ${namaAlat}: ${error.message}`);
  }
};

/**
 * Updates the stock of a glassware item upon return.
 * @param {string} namaAlat - The name of the glassware.
 * @param {number} jumlahDikembalikan - The quantity being returned.
 */
/**
 * Restores the stock of a material when a request is deleted.
 * Handles unit conversion between gram and mg.
 * @param {string} namaBahan - The name of the material.
 * @param {number} jumlahDiminta - The amount to restore.
 * @param {string} satuanDiminta - The unit of the amount (e.g., 'ml', 'gram', 'mg').
 */
export const restoreBahanStock = async (namaBahan, jumlahDiminta, satuanDiminta) => {
  if (!namaBahan || !jumlahDiminta || !satuanDiminta) {
    console.error("restoreBahanStock: Missing required parameters.");
    throw new Error("Parameter tidak lengkap untuk restore stok bahan.");
  }

  const bahanRef = collection(db, 'bahan');
  const q = query(bahanRef, where('nama', '==', namaBahan));

  try {
    await runTransaction(db, async (transaction) => {
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error(`Bahan "${namaBahan}" tidak ditemukan di database.`);
      }
      if (querySnapshot.size > 1) {
        console.warn(`Peringatan: Ditemukan ${querySnapshot.size} bahan dengan nama "${namaBahan}". Menggunakan yang pertama.`);
      }

      const bahanDocRef = querySnapshot.docs[0].ref;
      const bahanDoc = await transaction.get(bahanDocRef);

      if (!bahanDoc.exists()) {
        throw new Error(`Bahan "${namaBahan}" tidak ditemukan dalam transaksi.`);
      }

      const currentData = bahanDoc.data();
      const currentStock = Number(currentData.stok) || 0;
      const stockSatuan = currentData.satuan;

      let additionAmount = Number(jumlahDiminta);

      // Normalize units to lowercase for case-insensitive comparison
      const normalizedStockSatuan = stockSatuan.toLowerCase();
      const normalizedSatuanDiminta = satuanDiminta.toLowerCase();

      // Unit Conversion Logic (same as updateBahanStockOnApproval)
      if (normalizedStockSatuan === 'gram' && normalizedSatuanDiminta === 'mg') {
        additionAmount = additionAmount / 1000;
      } else if (normalizedStockSatuan === 'mg' && normalizedSatuanDiminta === 'gram') {
        additionAmount = additionAmount * 1000;
      } else if (normalizedStockSatuan !== normalizedSatuanDiminta && (normalizedStockSatuan === 'ml' || normalizedSatuanDiminta === 'ml')) {
        throw new Error(`Konversi satuan tidak didukung antara "${stockSatuan}" dan "${satuanDiminta}" untuk bahan "${namaBahan}".`);
      } else if (normalizedStockSatuan !== normalizedSatuanDiminta) {
        console.warn(`Satuan stok (${stockSatuan}) berbeda dengan satuan diminta (${satuanDiminta}) untuk ${namaBahan}, namun bukan gram/mg. Menggunakan jumlah diminta secara langsung.`);
      }

      const newStock = currentStock + additionAmount;

      transaction.update(bahanDocRef, { stok: newStock });
      console.log(`Stok bahan "${namaBahan}" berhasil dikembalikan menjadi ${newStock} ${stockSatuan}.`);
    });
  } catch (error) {
    console.error(`Error restoring stock for material "${namaBahan}":`, error);
    throw new Error(`Gagal mengembalikan stok untuk ${namaBahan}: ${error.message}`);
  }
};

/**
 * Restores the stock of a glassware item when an approval is deleted.
 * @param {string} namaAlat - The name of the glassware.
 * @param {number} jumlah - The quantity to restore.
 */
export const restoreAlatGelasStockOnDelete = async (namaAlat, jumlah) => {
  if (!namaAlat || jumlah === undefined || jumlah === null) {
    console.error("restoreAlatGelasStockOnDelete: Missing required parameters.");
    throw new Error("Parameter tidak lengkap untuk restore stok alat gelas.");
  }
  
  const amountToRestore = Number(jumlah);
  if (isNaN(amountToRestore) || amountToRestore <= 0) {
    throw new Error("Jumlah untuk dikembalikan harus angka positif.");
  }

  const alatRef = collection(db, 'alat_gelas');
  const q = query(alatRef, where('nama', '==', namaAlat));

  try {
    // Query to find the document
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error(`Alat gelas "${namaAlat}" tidak ditemukan di database.`);
      }
      
      if (querySnapshot.size > 1) {
        console.warn(`Peringatan: Ditemukan ${querySnapshot.size} alat gelas dengan nama "${namaAlat}". Menggunakan yang pertama.`);
      }

      const alatDocRef = querySnapshot.docs[0].ref;
    
    // Read current stock (outside transaction to avoid quota issues)
    const alatDoc = await getDoc(alatDocRef);

      if (!alatDoc.exists()) {
      throw new Error(`Alat gelas "${namaAlat}" tidak ditemukan.`);
      }

      const currentData = alatDoc.data();
      const currentStock = Number(currentData.stok) || 0;
      const newStock = currentStock + amountToRestore;

    // Update directly without transaction (simpler and avoids quota exceeded)
    await updateDoc(alatDocRef, { stok: newStock });

    console.log(`Stok alat gelas "${namaAlat}" berhasil dikembalikan dari ${currentStock} menjadi ${newStock} setelah penghapusan persetujuan.`);
  } catch (error) {
    console.error(`Error restoring stock for glassware "${namaAlat}" on delete:`, error);
    throw new Error(`Gagal mengembalikan stok untuk ${namaAlat}: ${error.message}`);
  }
};

export const updateAlatGelasStockOnReturn = async (namaAlat, jumlahDikembalikan, alatId = null) => {
   if (!namaAlat || jumlahDikembalikan === undefined || jumlahDikembalikan === null) {
    console.error("updateAlatGelasStockOnReturn: Missing required parameters.");
    throw new Error("Parameter tidak lengkap untuk update stok alat gelas.");
  }
   const amountToIncrease = Number(jumlahDikembalikan);
   if (isNaN(amountToIncrease) || amountToIncrease <= 0) {
     throw new Error("Jumlah dikembalikan harus angka positif.");
  }

  try {
    let alatDocRef = null;

    // If alatId is provided, use it directly (more reliable)
    if (alatId) {
      alatDocRef = doc(db, 'alat_gelas', alatId);
      const alatDoc = await getDoc(alatDocRef);
      
      if (!alatDoc.exists()) {
        // Fallback to name search if ID doesn't exist
        console.warn(`Alat gelas dengan ID "${alatId}" tidak ditemukan, mencoba mencari berdasarkan nama.`);
        alatDocRef = null;
      } else {
        // Found by ID, proceed
        const currentData = alatDoc.data();
        const currentStock = Number(currentData.stok) || 0;
        const newStock = currentStock + amountToIncrease;
        await updateDoc(alatDocRef, { stok: newStock });
        console.log(`Stok alat gelas "${namaAlat}" (ID: ${alatId}) berhasil diperbarui dari ${currentStock} menjadi ${newStock} setelah pengembalian.`);
        return;
      }
    }

    // Fallback: Search by name (normalize for better matching)
    if (!alatDocRef) {
      const alatRef = collection(db, 'alat_gelas');
      // Normalize namaAlat for search (trim and use exact match)
      const normalizedNama = namaAlat.trim();
      const q = query(alatRef, where('nama', '==', normalizedNama));

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // Try case-insensitive search by fetching all and filtering client-side
        const allAlatSnapshot = await getDocs(alatRef);
        const matchingDoc = allAlatSnapshot.docs.find(doc => 
          doc.data().nama?.trim().toLowerCase() === normalizedNama.toLowerCase()
        );
        
        if (matchingDoc) {
          alatDocRef = matchingDoc.ref;
        } else {
          throw new Error(`Alat gelas "${namaAlat}" tidak ditemukan di database untuk dikembalikan.`);
        }
      } else {
        if (querySnapshot.size > 1) {
          console.warn(`Peringatan: Ditemukan ${querySnapshot.size} alat gelas dengan nama "${namaAlat}". Menggunakan yang pertama.`);
        }
        alatDocRef = querySnapshot.docs[0].ref;
      }
    }
    
    // Read current stock (outside transaction to avoid quota issues)
    const alatDoc = await getDoc(alatDocRef);

    if (!alatDoc.exists()) {
      throw new Error(`Alat gelas "${namaAlat}" tidak ditemukan.`);
    }

    const currentData = alatDoc.data();
    const currentStock = Number(currentData.stok) || 0;
    const newStock = currentStock + amountToIncrease;

    // Update directly without transaction (simpler and avoids quota exceeded)
    await updateDoc(alatDocRef, { stok: newStock });

    console.log(`Stok alat gelas "${namaAlat}" berhasil diperbarui dari ${currentStock} menjadi ${newStock} setelah pengembalian.`);
  } catch (error) {
    console.error(`Error updating stock for glassware "${namaAlat}":`, error);
    throw new Error(`Gagal memperbarui stok untuk ${namaAlat} saat pengembalian: ${error.message}`);
  }
};


export default app;
